import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchResourceWithPost } from '@app/src/api/fetchHooks'
import CreateNewRequestModal from '@app/src/components/CreateNewRequestModal'
import TemplateAiScreeningStarterModal from '@app/src/components/CreateNewRequestModal/TemplateAiScreeningStarterModal'
import Avatar from '@app/src/components/Ui/Avatar'
import { QuestionnaireType } from '@app/src/context/CreationModalProgressContext'
import CreationModalProgressContextProvider from '@app/src/context/CreationModalProgressContextProvider'
import { useDialogState } from '@app/src/hooks/mui-hooks'
import useAiScreeningEnabled from '@app/src/hooks/useAiScreeningEnabled'
import { useBetterQueryParams } from '@app/src/hooks/queryParams'
import useBaselineAssessmentTemplate from '@app/src/pages/Configurations/ConfigurationsPages/Automation/Hooks/useBaselineAssessmentTemplate'
import TemplateTypeCard from '@app/src/pages/Configurations/SustainabilityLibraryPages/Questionnaires/TemplateTypeCard'
import {
  createTemplateTypeAvatar,
  getTemplateTypeConfig,
  TemplateTypeId,
} from '@app/src/pages/Configurations/SustainabilityLibraryPages/Questionnaires/templateTypeConfig'
import SustainabilityLibraryCollection from '@app/src/pages/Configurations/SustainabilityLibraryPages/SustainabilityLibraryCollection'
import { SUSTAINABILITY_LIBRARY_PAGE_IDS } from '@app/src/pages/Configurations/SustainabilityLibraryScene'
import { ScrapingType, type QuestionnaireTemplate } from '@app/src/types/resourceExplorer'
import { Logos } from '@app/src/wf-constants'
import paths from '@app/src/wf-constants/paths'
import {
  AutoAwesomeOutlined,
  DescriptionOutlined,
  LoopOutlined,
  PlayArrowOutlined,
  SendOutlined,
} from '@mui/icons-material'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material'
import qs from 'qs'
import React from 'react'
import { useIntl } from 'react-intl'
import { generatePath, useHistory } from 'react-router'
import type { QuestionnaireQueryParams } from './QuestionnaireConfig'
import { QuestionnaireConfigTabs } from './QuestionnaireConfig'

type StandardQuestionnaireDetailsProps = {
  id: string
}

const StandardQuestionnaireDetails: React.FC<StandardQuestionnaireDetailsProps> = ({ id }) => {
  const { formatMessage } = useIntl()
  const history = useHistory()
  const [isCreateRequestDialogOpen, openCreateRequestDialog, closeCreateRequestDialog] = useDialogState(false)
  const [isAiScreeningDialogOpen, openAiScreeningDialog, closeAiScreeningDialog] = useDialogState(false)
  const isAiScreeningEnabled = useAiScreeningEnabled()
  const { isStandard: isStandardRaw } = useBetterQueryParams<QuestionnaireQueryParams>()
  const isStandard = String(isStandardRaw ?? true) === 'true'
  const { baselineAssessmentTemplate } = useBaselineAssessmentTemplate()
  const baselineQuestionnaireId = baselineAssessmentTemplate?.questionnaireTemplateId

  const {
    data: template,
    isLoading,
    isError,
  } = useFetchResourceWithPost<QuestionnaireTemplate>({
    endpoint: endpoints.templateById(id, isStandard),
    body: [
      'tags',
      'creatorOrganization.name',
      'sections.questions.questionType',
      'sections.questions.reportFlagRules',
      'assessmentQuestionnaireLink.assessmentTemplate.name',
      'assessmentQuestionnaireLink.assessmentTemplate.solutionAvailability',
    ],
    key: [FetchKey.OrganizationOwnedTemplates, id, String(isStandard), 'details'],
    options: {
      enabled: Boolean(id),
    },
  })

  const handlePreviewClick = () => {
    history.push({
      pathname: generatePath(paths.sustainabilityLibrary, {
        configurationsPage: SUSTAINABILITY_LIBRARY_PAGE_IDS.Questionnaires,
        configurationsSubPage: id,
        activeTabParam: QuestionnaireConfigTabs.Questionnaire,
      }),
      search: qs.stringify({ isStandard: true }),
    })
  }

  const isBaseline = template?.id === baselineQuestionnaireId

  const getTemplateTypes = () => {
    const types: Array<ReturnType<typeof getTemplateTypeConfig>> = []

    if (template?.scrapingType === ScrapingType.AdverseScraping || isBaseline) {
      types.push(getTemplateTypeConfig(TemplateTypeId.AiScreening))
    }

    if (template?.scrapingType !== ScrapingType.AdverseScraping || isBaseline) {
      types.push(getTemplateTypeConfig(TemplateTypeId.Questionnaire))
    }

    if (template?.assessmentQuestionnaireLink && template.assessmentQuestionnaireLink.length > 0) {
      types.push({
        ...getTemplateTypeConfig(TemplateTypeId.Assessment),
        sidetitleId: 'questionnaireConfig.reuseAnswers',
      })
    }

    return types
  }

  const sectionCount = template?.sections?.length || 0
  const questionCount = template?.sections?.reduce((total, section) => total + (section.questions?.length || 0), 0) || 0
  const templateTypes = getTemplateTypes()
  const isAiScreeningOnly = templateTypes.length === 1 && templateTypes[0]?.id === TemplateTypeId.AiScreening
  const visibleTags = template?.tags?.filter(tag => !tag.toLowerCase().includes('request')) ?? []

  if (isLoading) {
    return (
      <SustainabilityLibraryCollection enablePadding={{ right: true }}>
        <Box display="flex" justifyContent="center" alignItems="center" flexDirection="column" mt={4}>
          <CircularProgress />
        </Box>
      </SustainabilityLibraryCollection>
    )
  }

  if (!template || isError) {
    return (
      <SustainabilityLibraryCollection enablePadding={{ right: true }}>
        <Box display="flex" justifyContent="center" alignItems="center" flexDirection="column" mt={4}>
          <Typography variant="h3" gutterBottom>
            {formatMessage({ id: 'table.emptyStateTitle' })}
          </Typography>
          <Typography>{formatMessage({ id: 'table.errorTitle' })}</Typography>
        </Box>
      </SustainabilityLibraryCollection>
    )
  }

  return (
    <>
      <SustainabilityLibraryCollection
        enablePadding={{ right: true }}
        title={template.title || formatMessage({ id: 'general.noTitle' })}
      >
        <Stack bgcolor="grey.100">
          <Grid container spacing={4}>
            <Grid item xs={12} md={7} lg={8}>
              <Box bgcolor="common.white" p={3} borderRadius={2}>
                <Box>
                  <Typography variant="subtitle1">
                    {formatMessage({ id: 'questionnaireConfig.aboutTemplate' })}
                  </Typography>
                  <Typography color="text.secondary" pt={2}>
                    {template.description || formatMessage({ id: 'questionnaireConfig.noDescription' })}
                  </Typography>
                </Box>
                <Divider sx={{ my: 2, mt: 3, mb: 3 }} />

                <Box>
                  <Typography variant="subtitle1">
                    {formatMessage({ id: 'questionnaireConfig.templateType' })}
                  </Typography>
                  <Grid container spacing={3} mt={0.5}>
                    {templateTypes.map((type, index) => (
                      <Grid
                        item
                        xs={12}
                        sm={templateTypes.length === 1 ? 12 : templateTypes.length === 3 && index === 2 ? 12 : 6}
                        key={type.id}
                      >
                        <TemplateTypeCard
                          avatar={createTemplateTypeAvatar(type, { width: 40, height: 40 })}
                          sidetitleId={type.sidetitleId}
                          titleId={type.titleId}
                          descriptionId={type.descriptionId}
                          bulletPointIds={type.bulletPointIds}
                          chipIds={type.chipIds}
                          compact
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>

                <Box>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} mt={3}>
                    <Typography variant="subtitle1">{formatMessage({ id: 'configurations.questions' })}</Typography>
                    <Button
                      startIcon={<PlayArrowOutlined />}
                      onClick={handlePreviewClick}
                      sx={{ textTransform: 'none' }}
                    >
                      <Typography variant="subtitle2">
                        {formatMessage({ id: 'questionnaireConfig.preview' })}
                      </Typography>
                    </Button>
                  </Stack>

                  <Stack spacing={1.5}>
                    {template.sections?.map(section => (
                      <Box key={section.id} p={1} border={1} borderColor="grey.300" borderRadius={1}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="body2">
                            {section.title || formatMessage({ id: 'general.noTitle' })}
                          </Typography>
                          <Typography variant="body2">•</Typography>
                          <Typography variant="body2">
                            {formatMessage(
                              {
                                id: 'questionnaireConfig.questionCount',
                              },
                              { count: section.questions?.length || 0 },
                            )}
                          </Typography>
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                </Box>

                {(() => {
                  const otherAssessments = template.assessmentQuestionnaireLink?.filter(
                    link => link.assessmentTemplate?.name !== template.title,
                  )
                  return otherAssessments && otherAssessments.length > 0 ? (
                    <Box>
                      <Typography variant="subtitle1" mt={3} mb={2}>
                        {formatMessage({ id: 'questionnaireConfig.reuseAnswers' })}
                      </Typography>
                      <Stack spacing={1.5}>
                        {otherAssessments.map(link => (
                          <Box key={link.id} p={1} border={1} borderColor="grey.300" borderRadius={1}>
                            <Typography variant="body2">{link.assessmentTemplate?.name || ''}</Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  ) : null
                })()}
              </Box>
            </Grid>

            <Grid item xs={12} md={5} lg={4}>
              <Box bgcolor="common.white" borderRadius={2}>
                <List disablePadding>
                  <ListItem>
                    <ListItemIcon>
                      <Avatar
                        alt="Worldfavor"
                        src={Logos.WorldfavorLogoSquareFilledBlack}
                        sx={{ bgcolor: 'common.white' }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={formatMessage({ id: 'questionnaireConfig.publishedBy' })}
                      secondary={
                        template.createdAt
                          ? new Date(template.createdAt).toLocaleDateString(undefined, {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            })
                          : ''
                      }
                    />
                  </ListItem>

                  <ListItem>
                    <Button fullWidth variant="outlined" startIcon={<PlayArrowOutlined />} onClick={handlePreviewClick}>
                      {formatMessage({ id: 'questionnaireConfig.preview' })}
                    </Button>
                  </ListItem>

                  <ListItem>
                    {isBaseline ? (
                      <Stack spacing={2} width="100%">
                        {isAiScreeningEnabled && (
                          <Button
                            fullWidth
                            variant="contained"
                            onClick={openAiScreeningDialog}
                            sx={{
                              textTransform: 'none',
                              color: 'communication.dark',
                              bgcolor: 'communication.main',
                              boxShadow: 'none',
                              '&:hover': {
                                bgcolor: 'communication.light',
                                boxShadow: 'none',
                              },
                            }}
                          >
                            {formatMessage({ id: 'questionnaireConfig.aiScreen' })}
                          </Button>
                        )}
                        <Button
                          fullWidth
                          variant="contained"
                          onClick={openCreateRequestDialog}
                          sx={{
                            textTransform: 'none',
                          }}
                          startIcon={<SendOutlined />}
                        >
                          {formatMessage({ id: 'questionnaireConfig.sendRequest' })}
                        </Button>
                      </Stack>
                    ) : isAiScreeningOnly ? (
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={openAiScreeningDialog}
                        sx={{
                          textTransform: 'none',
                          color: 'communication.dark',
                          bgcolor: 'communication.main',
                          boxShadow: 'none',
                          '&:hover': {
                            bgcolor: 'communication.light',
                            boxShadow: 'none',
                          },
                        }}
                      >
                        {formatMessage({ id: 'questionnaireConfig.aiScreen' })}
                      </Button>
                    ) : (
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={openCreateRequestDialog}
                        sx={{
                          textTransform: 'none',
                        }}
                        startIcon={<SendOutlined />}
                      >
                        {formatMessage({ id: 'questionnaireConfig.sendRequest' })}
                      </Button>
                    )}
                  </ListItem>

                  {(isBaseline || isAiScreeningOnly) && (
                    <ListItem>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <AutoAwesomeOutlined sx={{ fontSize: 24, color: 'communication.dark' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={formatMessage({ id: 'questionnaireConfig.aiEnabled' })}
                        primaryTypographyProps={{ sx: { color: 'communication.dark' } }}
                      />
                    </ListItem>
                  )}
                  {template.scrapingType !== ScrapingType.AdverseScraping && (
                    <ListItem sx={{ pt: 0 }}>
                      <ListItemIcon sx={{ minWidth: 40 }}>
                        <LoopOutlined sx={{ fontSize: 24 }} />
                      </ListItemIcon>
                      <ListItemText primary={formatMessage({ id: 'questionnaireConfig.networkEnabledReporting' })} />
                    </ListItem>
                  )}
                  <ListItem sx={{ pt: 0 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <DescriptionOutlined sx={{ fontSize: 24 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={formatMessage(
                        {
                          id: 'questionnaireConfig.sectionCount',
                        },
                        { count: sectionCount },
                      )}
                    />
                  </ListItem>
                  <ListItem sx={{ pt: 0 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <DescriptionOutlined sx={{ fontSize: 24 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={formatMessage(
                        {
                          id: 'questionnaireConfig.questionCount',
                        },
                        { count: questionCount },
                      )}
                    />
                  </ListItem>
                </List>
              </Box>
              {visibleTags.length > 0 && (
                <Box mt={3}>
                  <Typography variant="subtitle2" fontWeight={600} mb={2}>
                    {formatMessage({ id: 'questionnaireConfig.templateTags' })}
                  </Typography>
                  <Stack direction="row" flexWrap="wrap" gap={1}>
                    {visibleTags.map(tag => (
                      <Chip key={tag} label={tag} size="small" sx={{ bgcolor: 'grey.200' }} />
                    ))}
                  </Stack>
                </Box>
              )}
            </Grid>
          </Grid>
        </Stack>
      </SustainabilityLibraryCollection>

      <CreationModalProgressContextProvider>
        <CreateNewRequestModal
          open={isCreateRequestDialogOpen}
          onClose={closeCreateRequestDialog}
          questionnaireType={QuestionnaireType.WF}
          disableInitialBack
          selectedQuestionnaireTemplates={[template]}
        />
      </CreationModalProgressContextProvider>

      <CreationModalProgressContextProvider>
        <TemplateAiScreeningStarterModal
          open={isAiScreeningDialogOpen}
          onClose={closeAiScreeningDialog}
          selectedQuestionnaireTemplates={[template]}
        />
      </CreationModalProgressContextProvider>
    </>
  )
}

export default StandardQuestionnaireDetails
